// Vue
import {mixins} from 'vue-class-component'
import {Vue, Component, Prop, Watch, Provide} from 'vue-property-decorator'

// Template
import template from './template.vue'

// Components
import {Icon} from './../icon/Icon'

// Mixins
import {FormInput} from './../../../mixins/form_input/FormInput'

@Component({
    name: 'Select',
    components: {Icon},
    mixins: [
        template,
    ]
})
export class Select extends mixins(FormInput) {

    public get shouldDisplayMultiValues() {
        return this.value.length && Object.keys(this.valueLabelMap).some(key => this.value.some(value => value == key))
    }

    public get filteredErrors() {
        return this.displayErrors.filter(error => error != '' && error != undefined && error != null )
    }

    public get selectClass() {
        return `${this.$appPrefix}-select--${this.theme ? this.theme : 'default'}`
    }

    public get disabledClass() {
        return this.disabled  ? `${this.$appPrefix}-select--disabled` : ''
    }

    public get errorClass() {
        return this.$slots['validation'] || this.filteredErrors.length > 0 ? `${this.$appPrefix}-select--has-error` : ''
    }

    private isDestoyed: boolean = false

    public valueLabelMap: { [key: string]: {label: string, disabled: boolean} } = {}

    // Search input
    public searchTerm: string = ''

    // Current focused value
    public currentFocusedValue: string = ''

    // Options open
    public isOptionsOpen: boolean = false
    public isOptionsUp: boolean = false

    @Prop([Array, String, Number, Boolean])
    public value: any

    @Prop()
    @Provide('selectTheme')
    public theme!: string

    @Prop()
    @Provide('selectMultiple')
    public multiple?: boolean

    @Prop({default: true})
    public search?: boolean

    @Prop()
    public disabled?: boolean

    @Prop([String, Number])
    public label?: any

    @Prop()
    public placeholder?: string

    @Prop({default: () => []})
    public displayErrors!: string[]

    @Prop()
    public name!: string

    @Prop()
    public title!: string

    @Prop()
    public img!: string

    @Prop({default: true})
    public showErrors!: boolean

    @Prop({default: false})
    public isLoading!: boolean

    @Prop({default: "0"})
    public tabindex!: string

    @Prop({default: null})
    public limit!: number | null

    @Prop({default: null})
    public forceOpenDirection!: string | null

    @Watch('value')
    public onValueChange(value: any[], oldValue: any[]) {
        this.broadcast('SelectOption', 'change', [value])
        this.broadcast('SelectGroup', 'change', [value])
        if (this.multiple && this.limit != null) {
            if (value.length >= this.limit) {
                this.broadcast('SelectOption', 'limitDisable', [true])
            } else {
                this.broadcast('SelectOption', 'limitDisable', [false])
            }
        }
    }

    @Watch('searchTerm')
    public watchSearchTerm(searchTerm) {

        this.broadcast('SelectOption', 'searchTermChange', [searchTerm])
    }

    // ==================================
    // Types
    // ==================================
    public $refs!: {
        select: HTMLElement
        head: HTMLElement
        search: HTMLElement
        list: HTMLElement
    }

    public created() {

        this.$on('change', this.handleChange)
        this.$on('registerLabel', this.registerLabel)
        this.$on('groupChange', this.handleGroupChange)
        window.addEventListener('click', this.closeOptions)
    }

    public beforeDestroy() {
        this.isDestoyed = true   
    }

    public destroyed() {
        this.$off('change', this.handleChange)
        this.$off('registerLabel', this.registerLabel)
        this.$off('groupChange', this.handleGroupChange)
        window.removeEventListener('click', this.closeOptions)
    }

    public toggleOptions() {

        if (!this.isOptionsOpen) {
            this.isOptionsUp = this.calculateOpenDirection();
            this.isOptionsOpen = true

            if (this.search) {

                this.$nextTick(() => {
                    if (this.isDestoyed || !this.$refs.search) return

                    this.$refs.search.focus()
                })
            }

            this.$emit('open')
        } else {
            this.isOptionsOpen = false
            this.isOptionsUp = false

            if (this.search) {

                this.$nextTick(() => {
                    if (this.isDestoyed || !this.$refs.search) return

                    this.searchTerm = ''
                    this.$refs.search.blur()
                    this.$refs.search.style.width = 1 + 'px'
                })
            }

            this.$nextTick(() => {
                this.$refs.head.focus()
            })
            
            this.currentFocusedValue = ''
            this.broadcast('SelectOption', 'focus', [''])

            this.$emit('close')
        }
    }

    private calculateOpenDirection() {
        if (this.forceOpenDirection) {
            return this.forceOpenDirection == 'up';
        }

        const fixedFooterElement = <HTMLElement>document.querySelector(`.${this.$appPrefix}-fixed-footer`)

        let selectBottom = 0;
        if (fixedFooterElement) {
            selectBottom = fixedFooterElement.offsetHeight
        }

        let selectMaxHeight = 250
        let canOpenToBottom = (this.$refs.head.getBoundingClientRect().bottom + selectMaxHeight + selectBottom) < window.innerHeight
        let canOpenToTop = (this.$refs.head.getBoundingClientRect().top - selectMaxHeight) > 0

        return !canOpenToBottom && canOpenToTop
    }

    private openOptions() {
        this.isOptionsUp = this.calculateOpenDirection();
        this.isOptionsOpen = true

        if (this.search) {

            this.$nextTick(() => {
                if (this.isDestoyed || !this.$refs.search) return

                this.$refs.search.focus()
            })
        }

        this.$emit('open')
    }

    public closeOptions(e?: any) {

        if (!this.isOptionsOpen) return

        if (!e || e.target.contains(this.$refs.select) || !this.$refs.select.contains(e.target)) {
            this.isOptionsOpen = false

            if (this.search) {

                this.$nextTick(() => {
                    if (this.isDestoyed || !this.$refs.search) return

                    this.searchTerm = ''
                    this.$refs.search.blur()
                    this.$refs.search.style.width = 1 + 'px'
                })
            }

            this.$nextTick(() => {
                this.$refs.head.focus()
            })

            this.currentFocusedValue = ''
            this.broadcast('SelectOption', 'focus', [''])
        }
    }

    public handleChange(value: any) {
        let values = this.value

        if (this.multiple) {
            if (values.includes(value)) {
                values.splice(values.indexOf(value), 1)
                this.$emit('deselect', value)
            } else {
                values.push(value)
                this.$emit('select', value)
            }

            this.$emit('input', values)
        } else {
            this.$emit('input', value)
            this.toggleOptions()
            this.$emit('select', value)
        }
    }

    public handleGroupChange(value: any[], selected: boolean) {
        if (!this.multiple) {
            //No Group-Change for non-multiple-selects
            return
        }

        let values = this.value
        if( selected )
        {
            value.forEach( ( val ) => {
                if (!values.includes(val)) {
                    values.push(val)
                    this.$emit('select', val )
                }
            });
        } else {
            value.forEach( ( val ) => {
                if (values.includes(val)) {
                    values.splice(values.indexOf(val), 1)
                    this.$emit('deselect', val)
                }
            });
        }
        this.$emit('input', values)
    }

    private handleSpaceBar() {
        
        if (this.currentFocusedValue != '') {
            this.handleChange(this.currentFocusedValue)
        }
    }

    public handleRemoveSelected(value: any) {

        if (this.valueLabelMap[value].disabled) return

        let values = this.value

        if (values.includes(value)) {
            values.splice(values.indexOf(value), 1)
        }

        this.$emit('input', values)
        this.$emit('deselect', value)
    }

    public handleSearchTermInput(e: Event) {

        if (this.$refs.search.offsetWidth < (this.$refs.head.offsetWidth - 52)) {
            this.$refs.search.style.width = ((this.searchTerm.length + 1) * 8) + 'px'
        }

        this.$emit('search', this.searchTerm)
    }

    public registerLabel(value: string, label: any, disabled: boolean) {
        Vue.set(this.valueLabelMap, value, {label, disabled})
        this.broadcast('SelectOption', 'change', [this.value])
        this.broadcast('SelectGroup', 'change', [this.value])
    }

    private handleCancelSelect() {
        this.$emit('input', '')
    }

    private prev() {

        let optionValues = Object.keys(this.valueLabelMap)

        if (optionValues.length == 0) {
            this.currentFocusedValue = ''
            return
        }

        if (this.currentFocusedValue == '' &&
            (this.searchTerm.trim().length == 0 || this.valueLabelMap[optionValues[optionValues.length - 1]].label.trim().toLowerCase().indexOf(this.searchTerm.trim().toLowerCase()) != -1) &&
            !this.valueLabelMap[optionValues[optionValues.length - 1]].disabled) {

            this.currentFocusedValue = optionValues[optionValues.length - 1]
            this.broadcast('SelectOption', 'focus', [optionValues[optionValues.length - 1]])
        } else {

            const currentFocusedValueIndex = optionValues.findIndex(optionValue => optionValue == this.currentFocusedValue)

            if (currentFocusedValueIndex == 0 &&
                (this.searchTerm.trim().length == 0 || this.valueLabelMap[optionValues[optionValues.length - 1]].label.trim().toLowerCase().indexOf(this.searchTerm.trim().toLowerCase()) != -1) &&
                !this.valueLabelMap[optionValues[optionValues.length - 1]].disabled) {

                this.currentFocusedValue = optionValues[optionValues.length - 1]
                this.broadcast('SelectOption', 'focus', [optionValues[optionValues.length - 1]])
            } else {

                optionValues = optionValues.slice(0, currentFocusedValueIndex).reverse()

                for (const optionValue of optionValues) {

                    if ((this.searchTerm.trim().length == 0 || this.valueLabelMap[optionValue].label.trim().toLowerCase().indexOf(this.searchTerm.trim().toLowerCase()) != -1) &&
                        !this.valueLabelMap[optionValue].disabled) {

                        this.currentFocusedValue = optionValue
                        this.broadcast('SelectOption', 'focus', [optionValue])
                        return
                    }
                }
            }
        }
    }

    private next() {

        let optionValues = Object.keys(this.valueLabelMap)

        if (optionValues.length == 0) {
            this.currentFocusedValue = ''
            return
        } 

        if (this.currentFocusedValue == '') {

            for (const optionValue of optionValues) {

                if ((this.searchTerm.trim().length == 0 || this.valueLabelMap[optionValue].label.trim().toLowerCase().indexOf(this.searchTerm.trim().toLowerCase()) != -1) && 
                    !this.valueLabelMap[optionValue].disabled) {

                    this.broadcast('SelectOption', 'focus', [optionValue])
                    this.currentFocusedValue = optionValue
                    return
                }
            }

        } else {

            const currentFocusedValueIndex = optionValues.findIndex(optionValue => optionValue == this.currentFocusedValue)

            if (optionValues.length == currentFocusedValueIndex + 1 && 
                (this.searchTerm.trim().length == 0 || this.valueLabelMap[optionValues[0]].label.trim().toLowerCase().indexOf(this.searchTerm.trim().toLowerCase()) != -1) &&
                !this.valueLabelMap[optionValues[0]].disabled) {

                this.currentFocusedValue = optionValues[0]
                this.broadcast('SelectOption', 'focus', [optionValues[0]])
            } else {

                optionValues = optionValues.slice(currentFocusedValueIndex + 1)

                for (const optionValue of optionValues) {

                    if ((this.searchTerm.trim().length == 0 || this.valueLabelMap[optionValue].label.trim().toLowerCase().indexOf(this.searchTerm.trim().toLowerCase()) != -1) &&
                        !this.valueLabelMap[optionValue].disabled) {

                        this.currentFocusedValue = optionValue
                        this.broadcast('SelectOption', 'focus', [optionValue])
                        return
                    }
                }
            }
        }
    }
}